/** PROBATION-REVIEW-001 P2 — compact shared primitives (pill, segmented, progress). */
import React from 'react';
import { formatDateOnly } from '../../../../utils/formatters';
import './probation.css';

export const PROB_STATUS = {
  pending_verification: { label: 'Pending verification', cls: 'prb-slate' },
  in_progress: { label: 'In progress', cls: 'prb-blue' },
  due: { label: 'Due', cls: 'prb-amber' },
  overdue: { label: 'Overdue', cls: 'prb-rose' },
  awaiting_hr: { label: 'Awaiting HR', cls: 'prb-violet' },
  extended: { label: 'Extended', cls: 'prb-indigo' },
  confirmed: { label: 'Confirmed', cls: 'prb-emerald' },
  closed: { label: 'Closed', cls: 'prb-slate' },
};

export const REVIEW_STATUS = {
  scheduled: { label: 'Scheduled', cls: 'prb-slate' }, open: { label: 'Open', cls: 'prb-blue' },
  submitted: { label: 'Submitted', cls: 'prb-violet' }, approved: { label: 'Approved', cls: 'prb-emerald' },
  returned: { label: 'Returned', cls: 'prb-amber' }, superseded: { label: 'Superseded', cls: 'prb-slate' },
};

const dayDiff = (iso) => iso ? Math.round((new Date(`${iso}T00:00:00`) - new Date(new Date().toDateString())) / 86400000) : null;

/** Date-derive due/overdue so list rows never show a stale cached status. */
export const deriveProbation = (p) => {
  if (!p?.tracked) return null;
  let status = p.status;
  const days = dayDiff(p.end_date);
  if (p.verified && !['confirmed', 'closed', 'awaiting_hr'].includes(status) && days !== null) {
    status = days < 0 ? 'overdue' : days <= 14 ? 'due' : (p.extensions_count > 0 ? 'extended' : 'in_progress');
  }
  return { ...p, status, days_remaining: days };
};

export const ProbationPill = ({ probation, compact = false, testid }) => {
  const p = deriveProbation(probation);
  if (!p) return null;
  const m = PROB_STATUS[p.status] || PROB_STATUS.closed;
  const tail = p.status === 'extended' ? ` ×${p.extensions_count}` : '';
  const sub = compact && p.days_remaining !== null && ['in_progress', 'extended', 'due'].includes(p.status) ? ` · ${p.days_remaining}d` : '';
  return (
    <span className={`prb-pill ${m.cls}`} data-testid={testid || 'probation-pill'} data-status={p.status} title={`Probation ends ${p.end_date}`}>
      <i className="prb-dot" />{m.label}{tail}{sub}
    </span>
  );
};

export const StatusPill = ({ map, status, testid }) => {
  const m = map[status] || { label: status, cls: 'prb-slate' };
  return <span className={`prb-pill ${m.cls}`} data-testid={testid}><i className="prb-dot" />{m.label}</span>;
};

export const Segmented = ({ options, value, onChange, disabled, testid }) => (
  <div className="prb-seg" role="radiogroup" data-testid={testid}>
    {options.map(o => (
      <button key={o.value} type="button" role="radio" aria-checked={value === o.value} disabled={disabled} onClick={() => onChange(o.value)}
        className={`prb-seg-btn ${value === o.value ? 'on' : ''}`} data-testid={`${testid}-${o.value}`}>{o.label}</button>
    ))}
  </div>
);

export const ProgressStrip = ({ start, end, status }) => {
  const total = Math.max(1, dayDiff(end) - dayDiff(start));
  const done = Math.min(total, Math.max(0, -dayDiff(start)));
  const pct = status === 'confirmed' ? 100 : Math.round((done / total) * 100);
  return (
    <div className="prb-progress" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} data-testid="probation-progress">
      <span className={`prb-progress-fill ${status === 'overdue' ? 'late' : ''}`} style={{ width: `${Math.min(100, pct)}%` }} />
    </div>
  );
};

export const fmtDate = (iso) => (iso ? formatDateOnly(iso) : '—');
